import { postToAgents } from "./PlaybookApi";

const PlaybookEditService = {
	WHO_DECIDES: Object.freeze({ CLIENT_MANAGERS: "client_managers", ROLES: "roles" }),

	/**
	 * One agent as the viewer may see it: base instructions only if allowed, every addition with
	 * its author.
	 *
	 * @param {Object} params { playbookId }
	 * @return {Promise<Object>}
	 */
	getPlaybookForEditing({ playbookId }) {
		return postToAgents({ operationId: "getPlaybookForEditing", body: { playbookId } });
	},

	/**
	 * Every version of an agent, newest first, with what each changed.
	 *
	 * @param {Object} params { playbookId }
	 * @return {Promise<Array>}
	 */
	listVersions({ playbookId }) {
		return postToAgents({ operationId: "listPlaybookVersions", body: { playbookId } });
	},

	/**
	 * Adds an instruction to an agent as a new version credited to the viewer.
	 *
	 * @param {Object} params { playbookId, text, changeNote }
	 * @return {Promise<Object>} the agent as the viewer sees it
	 */
	addAddition({ playbookId, text, changeNote }) {
		return postToAgents({ operationId: "addPlaybookAddition", body: { playbookId, text, changeNote } });
	},

	/**
	 * Rewrites one of the viewer's own additions.
	 *
	 * @param {Object} params { playbookId, additionId, text, changeNote }
	 * @return {Promise<Object>}
	 */
	editAddition({ playbookId, additionId, text, changeNote }) {
		return postToAgents({ operationId: "editPlaybookAddition", body: { playbookId, additionId, text, changeNote } });
	},

	/**
	 * Takes one of the viewer's own additions out of the agent.
	 *
	 * @param {Object} params { playbookId, additionId, changeNote }
	 * @return {Promise<Object>}
	 */
	removeAddition({ playbookId, additionId, changeNote }) {
		return postToAgents({ operationId: "removePlaybookAddition", body: { playbookId, additionId, changeNote } });
	},

	/**
	 * Rewrites an agent's base instructions; its author or an administrator only.
	 *
	 * @param {Object} params { playbookId, baseInstructions, changeNote }
	 * @return {Promise<Object>}
	 */
	editBase({ playbookId, baseInstructions, changeNote }) {
		return postToAgents({ operationId: "editPlaybookBaseInstructions", body: { playbookId, baseInstructions, changeNote } });
	},

	/**
	 * Goes back to an earlier version by saving a new one. Resolves to { agent, keptAdditionAuthors }.
	 *
	 * @param {Object} params { playbookId, toVersionId, changeNote }
	 * @return {Promise<Object>}
	 */
	rollBack({ playbookId, toVersionId, changeNote }) {
		return postToAgents({ operationId: "rollBackPlaybookVersion", body: { playbookId, toVersionId, changeNote } });
	},

	/**
	 * An agent's settings panel: current settings, who each role resolves to per team, history,
	 * the firm's roles and teams, and whether the viewer may change them.
	 *
	 * @param {Object} params { playbookId }
	 * @return {Promise<Object>}
	 */
	getSettings({ playbookId }) {
		return postToAgents({ operationId: "getPlaybookSettings", body: { playbookId } });
	},

	/**
	 * One cell of the settings coverage table: who decides or signs off for a team's clients, and
	 * how many of its clients name nobody (only counted when the client's own managers act).
	 *
	 * @param {Object} params
	 * @param {Array<String>} params.names the people who act
	 * @param {Number|null} params.clientsWithNobody clients naming nobody, or null when roles decide
	 * @param {Boolean} params.isRequired false when sign-off is turned off
	 * @return {String}
	 */
	describeCoverageCell({ names, clientsWithNobody, isRequired }) {
		if (!isRequired) return "Not required";
		const hasNames = names.length > 0;
		const namesText = hasNames ? names.join(", ") : "Nobody";
		const hasClientsWithNobody = Number(clientsWithNobody) > 0;
		if (!hasClientsWithNobody) return namesText;
		const clientWord = clientsWithNobody === 1 ? "client names" : "clients name";
		const gapText = `${clientsWithNobody} ${clientWord} nobody`;
		const cellText = hasNames ? `${namesText} · ${gapText}` : gapText;
		return cellText;
	},

	/**
	 * Saves an agent's settings as a new version.
	 *
	 * @param {Object} params { playbookId, settings, changeNote }
	 * @return {Promise<Object>}
	 */
	saveSettings({ playbookId, settings, changeNote }) {
		return postToAgents({ operationId: "savePlaybookSettings", body: { playbookId, settings, changeNote } });
	},

	/**
	 * Takes a playbook off the firm's list. Its versions, runs and results stay, and who archived
	 * it and when is recorded.
	 *
	 * @param {Object} params { playbookId }
	 * @return {Promise<Object>} { playbookId }
	 */
	archivePlaybook({ playbookId }) {
		return postToAgents({ operationId: "archivePlaybook", body: { playbookId } });
	},

	/**
	 * Plain words for what kind of change made a version.
	 *
	 * @param {Object} params
	 * @param {String} params.changeKind
	 * @return {String}
	 */
	describeChangeKind({ changeKind }) {
		const descriptions = {
			created: "Built",
			addition_added: "Addition",
			addition_edited: "Addition changed",
			addition_removed: "Addition removed",
			base_edited: "Base instructions changed",
			rolled_back: "Rolled back",
			builtin_updated: "SRJ update",
		};
		return descriptions[changeKind] || changeKind;
	},
};

export default PlaybookEditService;
